/**
 * イメージボードの手描き（Apple Pencil / マウス）の状態と入力処理。
 *
 * ImageBoard 本体から描画まわりだけを切り出したフック。ポインタの受け取り、
 * 描画中の点の蓄積、保存、消しゴム、Undo を持つ。
 *
 * iPad での操作の分担:
 * - Apple Pencil / マウス → 描く・消す
 * - 指（touch）→ 描かずにパン / ピンチへ回す（描画モード中でも移動できる）
 */
import { useCallback, useMemo, useRef, useState, type PointerEvent } from "react";
import toast from "react-hot-toast";

import {
  useCreateImageBoardStrokeMutation,
  useDeleteImageBoardStrokesMutation,
  type ImageBoardStroke,
  type StrokePoint,
} from "../../app/services/imageBoard";
import { DEFAULT_PRESSURE, isStrokeHit, simplifyStroke } from "./strokeGeometry";

export type BoardTool = "select" | "pen" | "eraser";

/**
 * ペンの色。SOREMO のブランドカラー（サインイン背景の FlowingCircles と同じ
 * 5 色）を使う。既定色は白いキャンバスでも読める濃い青にしたいので、
 * 濃い順に並べて先頭を既定にしている。
 */
export const PEN_COLORS = [
  { value: "#0066CC", label: "ブルー" },
  { value: "#E71D36", label: "レッド" },
  { value: "#FF6B35", label: "オレンジ" },
  { value: "#00B4D8", label: "シアン" },
  { value: "#FFE100", label: "イエロー" },
] as const;

/** ペンの太さ（ワールド単位）。 */
export const PEN_WIDTHS = [2, 4, 8, 16] as const;

/** 消しゴムの判定半径（ワールド単位）。 */
const ERASER_RADIUS = 12;

/**
 * 点を間引く最小移動距離（画面上のピクセル）。
 * ワールド単位へはズーム倍率で割って換算するので、拡大していても
 * 見た目の滑らかさが変わらない。
 */
const MIN_POINT_DISTANCE_PX = 2;

/** 1 ストロークの点数上限（BE の MAX_POINTS_PER_STROKE と合わせる）。 */
const MAX_POINTS_PER_STROKE = 5000;

/** Undo で戻れる回数。自分がこのセッションで描いたぶんだけ戻せる。 */
const MAX_UNDO_DEPTH = 50;

interface UseBoardDrawingArgs {
  noteId: string | undefined;
  strokes: ImageBoardStroke[];
  tool: BoardTool;
  color: string;
  width: number;
  /** 現在のズーム倍率（点の間引き幅の換算に使う）。 */
  scale: number;
  // eslint-disable-next-line no-unused-vars
  screenToWorld: (clientX: number, clientY: number) => { x: number; y: number };
}

export const useBoardDrawing = ({
  noteId,
  strokes,
  tool,
  color,
  width,
  scale,
  screenToWorld,
}: UseBoardDrawingArgs) => {
  const [createStroke] = useCreateImageBoardStrokeMutation();
  const [deleteStrokes] = useDeleteImageBoardStrokesMutation();

  const [livePoints, setLivePoints] = useState<StrokePoint[] | null>(null);
  const [pendingEraseIds, setPendingEraseIds] = useState<Set<string>>(new Set());

  // 描画中の点。setState だけだと 1 フレームに何度も来る move で取りこぼすため、
  // 正はこの ref に持ち、表示用に state へ複製する。
  const livePointsRef = useRef<StrokePoint[]>([]);
  const drawingPointerRef = useRef<number | null>(null);
  const erasingRef = useRef<Set<string>>(new Set());
  // このセッションで自分が作ったストロークの id（Undo 用）。
  const undoStackRef = useRef<string[]>([]);
  // 最新の strokes を消しゴム判定から参照するための鏡。
  const strokesRef = useRef(strokes);
  strokesRef.current = strokes;

  const [canUndo, setCanUndo] = useState(false);

  const minPointDistance = MIN_POINT_DISTANCE_PX / Math.max(scale, 0.01);

  /** このポインタで描く（消す）べきか。指はパンに回す。 */
  const shouldDraw = useCallback(
    (pointerType: string) => tool !== "select" && pointerType !== "touch",
    [tool],
  );

  const collectErasures = useCallback(
    (worldX: number, worldY: number) => {
      const radius = ERASER_RADIUS / Math.max(scale, 0.01);
      let added = false;
      for (const stroke of strokesRef.current) {
        if (erasingRef.current.has(stroke.id)) continue;
        if (isStrokeHit(stroke.points, worldX, worldY, radius)) {
          erasingRef.current.add(stroke.id);
          added = true;
        }
      }
      if (added) setPendingEraseIds(new Set(erasingRef.current));
    },
    [scale],
  );

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      if (!shouldDraw(e.pointerType) || drawingPointerRef.current !== null) {
        return false;
      }
      drawingPointerRef.current = e.pointerId;
      const { x, y } = screenToWorld(e.clientX, e.clientY);

      if (tool === "eraser") {
        erasingRef.current = new Set();
        collectErasures(x, y);
        return true;
      }

      const pressure = e.pressure > 0 ? e.pressure : DEFAULT_PRESSURE;
      livePointsRef.current = [[x, y, pressure]];
      setLivePoints(livePointsRef.current);
      return true;
    },
    [collectErasures, screenToWorld, shouldDraw, tool],
  );

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (drawingPointerRef.current !== e.pointerId) return false;
      const { x, y } = screenToWorld(e.clientX, e.clientY);

      if (tool === "eraser") {
        collectErasures(x, y);
        return true;
      }

      const points = livePointsRef.current;
      const last = points[points.length - 1];
      if (last) {
        const dx = x - last[0];
        const dy = y - last[1];
        if (dx * dx + dy * dy < minPointDistance * minPointDistance) return true;
      }
      if (points.length >= MAX_POINTS_PER_STROKE) return true;

      const pressure = e.pressure > 0 ? e.pressure : DEFAULT_PRESSURE;
      livePointsRef.current = [...points, [x, y, pressure]];
      setLivePoints(livePointsRef.current);
      return true;
    },
    [collectErasures, minPointDistance, screenToWorld, tool],
  );

  const commitErase = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0 || !noteId) return;
      try {
        await deleteStrokes({ note_id: noteId, ids }).unwrap();
        // 消したものは Undo 対象から外す（復活はさせない）
        const removed = new Set(ids);
        undoStackRef.current = undoStackRef.current.filter(
          (id) => !removed.has(id),
        );
        setCanUndo(undoStackRef.current.length > 0);
      } catch {
        toast.error("線の削除に失敗しました");
      }
    },
    [deleteStrokes, noteId],
  );

  const commitStroke = useCallback(
    async (points: StrokePoint[]) => {
      // 点 1 つ（タップしただけ）は線にならないので捨てる
      if (points.length < 2 || !noteId) return;
      try {
        const result = await createStroke({
          note_id: noteId,
          points,
          color,
          width,
        }).unwrap();
        undoStackRef.current = [
          ...undoStackRef.current,
          result.data.stroke.id,
        ].slice(-MAX_UNDO_DEPTH);
        setCanUndo(true);
      } catch {
        toast.error("線の保存に失敗しました");
      }
    },
    [color, createStroke, noteId, width],
  );

  /**
   * ポインタを離したときの確定。
   *
   * 「このイベントを描画側で処理したか」を呼び出し側が同期的に判定できるよう、
   * 保存の完了は待たずに真偽値を返す（await すると Promise が返り、
   * 呼び出し側の分岐が常に真になってしまう）。
   */
  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      if (drawingPointerRef.current !== e.pointerId) return false;
      drawingPointerRef.current = null;

      if (tool === "eraser") {
        const ids = Array.from(erasingRef.current);
        erasingRef.current = new Set();
        setPendingEraseIds(new Set());
        void commitErase(ids);
        return true;
      }

      const points = simplifyStroke(livePointsRef.current, minPointDistance);
      livePointsRef.current = [];
      setLivePoints(null);
      void commitStroke(points);
      return true;
    },
    [commitErase, commitStroke, minPointDistance, tool],
  );

  /** 描画中に中断（ポインタキャンセル）されたときの後始末。 */
  const onPointerCancel = useCallback((e: PointerEvent) => {
    if (drawingPointerRef.current !== e.pointerId) return false;
    drawingPointerRef.current = null;
    livePointsRef.current = [];
    erasingRef.current = new Set();
    setLivePoints(null);
    setPendingEraseIds(new Set());
    return true;
  }, []);

  /** 直前に自分が引いた線を 1 本取り消す。 */
  const undo = useCallback(async () => {
    const id = undoStackRef.current[undoStackRef.current.length - 1];
    if (!id || !noteId) return;
    undoStackRef.current = undoStackRef.current.slice(0, -1);
    setCanUndo(undoStackRef.current.length > 0);
    try {
      await deleteStrokes({ note_id: noteId, ids: [id] }).unwrap();
    } catch {
      // 失敗したら取り消せていないので、戻せる状態に復帰させる
      undoStackRef.current = [...undoStackRef.current, id];
      setCanUndo(true);
      toast.error("取り消しに失敗しました");
    }
  }, [deleteStrokes, noteId]);

  return useMemo(
    () => ({
      livePoints,
      pendingEraseIds,
      canUndo,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      undo,
    }),
    [
      canUndo,
      livePoints,
      onPointerCancel,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      pendingEraseIds,
      undo,
    ],
  );
};

/** 描画で処理しなかった入力を回す先（キャンバスのパン / ピンチ）。 */
export interface PanHandlers {
  // eslint-disable-next-line no-unused-vars
  onPointerDown: (e: PointerEvent) => void;
  // eslint-disable-next-line no-unused-vars
  onPointerMove: (e: PointerEvent) => void;
  // eslint-disable-next-line no-unused-vars
  onPointerUp: (e: PointerEvent) => void;
}

/** `createDrawingSurfaceHandlers` が必要とする描画側の入力処理。 */
type DrawingPointerHandlers = Pick<
  ReturnType<typeof useBoardDrawing>,
  "onPointerDown" | "onPointerMove" | "onPointerUp" | "onPointerCancel"
>;

/**
 * 描画レイヤー（DrawingSurface）に付けるポインタハンドラを組み立てる。
 *
 * 描画レイヤーはキャンバスの子なので、何もしないと入力が親のパン処理へ
 * 伝播する。親は pointerdown で自分へ setPointerCapture するため、伝播させると
 * capture を奪われ、以降の pointermove が描画側へ届かない（点が 1 つしか
 * 溜まらず線が保存されない）。そのため描画が処理した入力は必ずここで止める。
 *
 * 逆に描画が処理しなかった入力（指でのパン / ピンチ）は、そのまま親の
 * ハンドラへ渡す。
 */
export const createDrawingSurfaceHandlers = (
  drawing: DrawingPointerHandlers,
  pan: PanHandlers,
) => ({
  onPointerDown: (e: PointerEvent) => {
    if (drawing.onPointerDown(e)) {
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    pan.onPointerDown(e);
  },
  onPointerMove: (e: PointerEvent) => {
    if (drawing.onPointerMove(e)) {
      e.stopPropagation();
      return;
    }
    pan.onPointerMove(e);
  },
  onPointerUp: (e: PointerEvent) => {
    if (drawing.onPointerUp(e)) {
      e.stopPropagation();
      return;
    }
    pan.onPointerUp(e);
  },
  onPointerCancel: (e: PointerEvent) => {
    if (drawing.onPointerCancel(e)) {
      e.stopPropagation();
      return;
    }
    pan.onPointerUp(e);
  },
});
